import { NavLink, Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Gauge,
  Boat,
  Users,
  GridFour,
  Bell,
  Gear,
  SignOut,
  Tray,
  IdentificationCard,
  FileText,
  Receipt,
  DeviceRotate,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/slots", label: "Θέσεις", icon: GridFour, id: "slots" },
  { to: "/boats", label: "Σκάφη", icon: Boat, id: "boats" },
  { to: "/owners", label: "Ιδιοκτήτες", icon: Users, id: "owners" },
  { to: "/inquiries", label: "Κρατήσεις", icon: Tray, id: "inquiries" },
  { to: "/renewals", label: "Ανανεώσεις", icon: FileText, id: "renewals" },
  { to: "/fiscal", label: "Παραστατικά", icon: Receipt, id: "fiscal" },
  { to: "/alerts", label: "Ειδοποιήσεις", icon: Bell, id: "alerts" },
  { to: "/overview", label: "Επισκόπηση", icon: Gauge, id: "overview" },
  { to: "/settings", label: "Ρυθμίσεις", icon: Gear, id: "settings" },
  { to: "/account", label: "Λογαριασμός", icon: IdentificationCard, id: "account" },
];

const MOBILE_TABS = [
  { to: "/slots", label: "Θέσεις", icon: GridFour, id: "slots" },
  { to: "/boats", label: "Σκάφη", icon: Boat, id: "boats" },
  { to: "/owners", label: "Ιδιοκτήτες", icon: Users, id: "owners" },
  { to: "/inquiries", label: "Αιτήματα", icon: Tray, id: "inquiries" },
];

export default function Layout({ config }) {
  const { logout } = useAuth();
  const { data: alerts } = useQuery({
    queryKey: ["alerts"],
    queryFn: async () => (await api.get("/alerts")).data,
    refetchInterval: 15000,
  });
  const alertCount = alerts ? alerts.expired.length + alerts.expiring.length : 0;
  const { data: inquiries } = useQuery({
    queryKey: ["inquiries"],
    queryFn: async () => (await api.get("/inquiries")).data,
    refetchInterval: 20000,
  });
  const newInquiries = inquiries ? inquiries.filter((i) => i.status === "new").length : 0;

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <div
        className="fixed inset-0 z-[100] hidden flex-col items-center justify-center gap-4 bg-[#183456] px-8 text-center text-white portrait:max-md:flex"
        data-testid="rotate-overlay"
      >
        <DeviceRotate size={56} weight="bold" className="text-white/90" />
        <div className="text-lg font-bold">Γυρίστε τη συσκευή σας</div>
        <p className="text-sm text-white/70">
          Η εφαρμογή σε κινητό λειτουργεί σε οριζόντια προβολή (landscape) για καλύτερη εμφάνιση των πινάκων.
        </p>
      </div>
      <aside
        className="relative hidden md:flex w-[270px] shrink-0 flex-col border-r border-border text-white"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(24,52,86,0.55) 0%, rgba(24,52,86,0.66) 55%, rgba(20,44,74,0.78) 100%), url('/sidebar-water.webp')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="sidebar"
      >
        <div className="border-b border-white/10 px-4 py-5">
          <img src="/navipark-logo-white.png" alt="NAVIPARK" className="h-auto w-full max-w-[180px]" />
          <div className="mt-2 truncate text-sm font-medium text-white">{config?.name}</div>
        </div>
        <nav className="flex-1 p-3">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.id}`}
                className={({ isActive }) =>
                  `mb-1 flex items-center gap-3 rounded-sm px-4 py-2.5 text-sm font-medium transition-colors duration-150 ${
                    isActive ? "bg-[#0055FF] text-white" : "text-white/60 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <Icon size={18} weight="bold" />
                {item.label}
                {item.id === "alerts" && alertCount > 0 && (
                  <span className="ml-auto rounded-sm bg-[#EF4444] px-1.5 py-0.5 text-[10px] font-bold tabular-nums" data-testid="alert-badge">{alertCount}</span>
                )}
                {item.id === "inquiries" && newInquiries > 0 && (
                  <span className="ml-auto rounded-sm bg-[#0055FF] px-1.5 py-0.5 text-[10px] font-bold tabular-nums" data-testid="inquiry-badge">{newInquiries}</span>
                )}
              </NavLink>
            );
          })}
          <button onClick={logout} data-testid="logout-btn" className="mb-1 flex w-full items-center gap-3 rounded-sm px-4 py-2.5 text-sm font-medium text-white/60 transition-colors duration-150 hover:bg-white/5 hover:text-white">
            <SignOut size={18} weight="bold" /> Αποσύνδεση
          </button>
        </nav>
        <div className="border-t border-white/10 p-3">
          <div className="px-3 py-2 text-[11px] text-white/40">NAVIPARK © 2026</div>
        </div>
      </aside>

      <div className="flex flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-border bg-white px-4 py-3 md:hidden" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
          <img src="/navipark-logo.png" alt="NAVIPARK" className="h-7 w-auto" data-testid="mobile-logo" />
          <div className="ml-auto flex items-center gap-4">
            <NavLink to="/account" data-testid="mobile-account-link" aria-label="Λογαριασμός NAVIPARK" className="text-[#234E7D]">
              <IdentificationCard size={26} weight="bold" />
            </NavLink>
            <button onClick={logout} data-testid="mobile-logout" aria-label="Αποσύνδεση" className="text-destructive">
              <SignOut size={26} weight="bold" />
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-x-hidden pb-20 md:pb-0">
          <Outlet />
        </main>

        <nav className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-4 border-t border-border bg-white/95 backdrop-blur md:hidden" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="mobile-tabbar">
          {MOBILE_TABS.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`tab-${item.id}`}
                className={({ isActive }) =>
                  `relative flex flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-medium transition-colors ${isActive ? "text-[#0055FF]" : "text-muted-foreground"}`
                }
              >
                <Icon size={22} weight="bold" />
                {item.label}
                {item.id === "inquiries" && newInquiries > 0 && (
                  <span className="absolute right-6 top-1 h-2 w-2 rounded-full bg-[#0055FF]" />
                )}
              </NavLink>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
